import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import type { Product } from '../data/products';

type WishlistItem = Product;

type WishlistContextValue = {
  wishlist: WishlistItem[];
  addToWishlist: (product: Product) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (product: Product) => void;
  wishlistCount: number;
};

type WishlistStateContextValue = Pick<WishlistContextValue, 'wishlist' | 'wishlistCount'>;
type WishlistActionsContextValue = Pick<
  WishlistContextValue,
  'addToWishlist' | 'removeFromWishlist' | 'isInWishlist' | 'toggleWishlist'
>;

const WishlistStateContext = createContext<WishlistStateContextValue | undefined>(undefined);
const WishlistActionsContext = createContext<WishlistActionsContextValue | undefined>(undefined);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    try {
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        const parsed = JSON.parse(savedWishlist);
        setWishlist(Array.isArray(parsed) ? parsed : []);
      }
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }, [wishlist, isInitialized]);

  const addToWishlist = useCallback((product: Product) => {
    if (wishlist.some((item) => item.id === product.id)) {
      toast.message('Энэ бараа аль хэдийн wishlist-д байна', {
        id: `wishlist-existing-${product.id}`,
      });
      return;
    }

    setWishlist((prev) => (
      prev.some((item) => item.id === product.id) ? prev : [...prev, product]
    ));
    toast.success(`${product.name} wishlist-д нэмэгдлээ!`, {
      id: `wishlist-add-${product.id}`,
    });
  }, [wishlist]);

  const removeFromWishlist = useCallback((productId: string) => {
    const item = wishlist.find((entry) => entry.id === productId);
    setWishlist((prev) => prev.filter((entry) => entry.id !== productId));

    if (item) {
      toast.error(`${item.name} wishlist-аас хасагдлаа`, {
        id: `wishlist-remove-${productId}`,
      });
    }
  }, [wishlist]);

  const isInWishlist = useCallback(
    (productId: string) => wishlist.some((item) => item.id === productId),
    [wishlist]
  );

  const toggleWishlist = useCallback((product: Product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  }, [addToWishlist, isInWishlist, removeFromWishlist]);

  const wishlistCount = useMemo(() => wishlist.length, [wishlist]);
  const stateValue = useMemo(
    () => ({
      wishlist,
      wishlistCount,
    }),
    [wishlist, wishlistCount]
  );
  const actionsValue = useMemo(
    () => ({
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
    }),
    [addToWishlist, removeFromWishlist, isInWishlist, toggleWishlist]
  );

  return (
    <WishlistStateContext.Provider value={stateValue}>
      <WishlistActionsContext.Provider value={actionsValue}>{children}</WishlistActionsContext.Provider>
    </WishlistStateContext.Provider>
  );
}

export const useWishlistState = () => {
  const context = useContext(WishlistStateContext);
  if (!context) throw new Error('useWishlistState must be used within a WishlistProvider');
  return context;
};

export const useWishlistActions = () => {
  const context = useContext(WishlistActionsContext);
  if (!context) throw new Error('useWishlistActions must be used within a WishlistProvider');
  return context;
};

export const useWishlist = () => ({
  ...useWishlistState(),
  ...useWishlistActions(),
});
